/**
 * Register Default Tab Renderers (DRR-165)
 *
 * Maps FormSchema renderer keys to React components for the detail panel.
 * Called once at app init (App.tsx). New renderers added here.
 *
 * Renderer keys match views.detail_tabs[].renderer in FormSchema config.
 */

import React from 'react';
import { registerTabRenderer, type TabRendererProps } from '@ferp/workflow-forms';
import { WorkflowContextTab } from './tabs/WorkflowContextTab';
import { WorkflowFlowTab } from './tabs/WorkflowFlowTab';
import { AuditTrailTab } from './tabs/AuditTrailTab';
import { RelationshipsTab } from './tabs/RelationshipsTab';
import { TimelineTab } from './tabs/TimelineTab';
import { InstanceOperationsTab } from './tabs/InstanceOperationsTab';

// Tab components expect PrimitiveItem (entity with id, name, etc.).
// TabRendererProps.entity is Record<string, unknown>.
// Cast is safe — the entity IS a PrimitiveItem from the Tabulator row.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyEntity = any;

function WorkflowContextRenderer({ entity, primitiveType }: TabRendererProps) {
  return React.createElement(WorkflowContextTab, { entity: entity as AnyEntity, primitiveType });
}

function WorkflowFlowRenderer({ entity, primitiveType }: TabRendererProps) {
  return React.createElement(WorkflowFlowTab, { entity: entity as AnyEntity, primitiveType });
}

function AuditTrailRenderer({ entity, primitiveType }: TabRendererProps) {
  return React.createElement(AuditTrailTab, { entity: entity as AnyEntity, primitiveType });
}

function RelationshipsRenderer({ entity, primitiveType }: TabRendererProps) {
  return React.createElement(RelationshipsTab, { entity: entity as AnyEntity, primitiveType });
}

function TimelineRenderer({ entity, primitiveType }: TabRendererProps) {
  return React.createElement(TimelineTab, { entity: entity as AnyEntity, primitiveType });
}

/**
 * Register all built-in tab renderers.
 * Call this once at app initialization.
 */
export function registerDefaultTabRenderers(): void {
  registerTabRenderer('workflow-state-viz', WorkflowContextRenderer);
  registerTabRenderer('workflow-flow-viz', WorkflowFlowRenderer);
  registerTabRenderer('transition-controls', WorkflowContextRenderer);
  registerTabRenderer('spawned-tasks-list', WorkflowContextRenderer);
  registerTabRenderer('config-edges', RelationshipsRenderer);
  registerTabRenderer('audit-trail', AuditTrailRenderer);
  registerTabRenderer('state-history', TimelineRenderer);
  registerTabRenderer('timeline', TimelineRenderer);
  registerTabRenderer('instance-operations', InstanceOperationsTab);
}
